import { CardMessageLengthPreview } from '@/components/checkout-settings/CardMessageLengthPreview'
import { BillingBooleanSettingField } from '@/components/customers/BillingBooleanSettingField'
import SaveBar from '@/components/forms/SaveBar'
import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useIsReadOnly } from '@/lib/casl'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import {
  checkoutSettingsQueries,
  useCheckoutSettings,
  useUpdateCheckoutSettingsMutation,
} from '@/lib/services/checkout-settings/checkout-settings.query'
import {
  CARD_MESSAGE_LIMIT_MAX,
  CARD_MESSAGE_LIMIT_MIN,
  checkoutSettingsFormSchema,
} from '@/lib/services/checkout-settings/checkout-settings.schema'
import type { CheckoutSettings } from '@/lib/services/checkout-settings/checkout-settings.types'
import { getErrorMessage } from '@/lib/utils'
import { createFileRoute, notFound } from '@tanstack/react-router'
import { Gift, HandCoins, ShoppingBasket, ShoppingCart, Split, Truck } from 'lucide-react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/settings/checkout/')({
  component: CheckoutSettingsPage,
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'checkout-settings')) throw notFound()

    await context.queryClient.ensureQueryData(
      checkoutSettingsQueries.detail({ organizationId: params.orgId })
    )

    return { isReadOnly: !context.ability.can('edit', 'checkout-settings') }
  },
})

export function CheckoutSettingsPage() {
  const { orgId } = Route.useParams()
  const { data: settings } = useCheckoutSettings({ organizationId: orgId })

  return (
    <div className="max-w-4xl space-y-5">
      <AdminPageHeader
        icon={ShoppingCart}
        title="Checkout"
        description="Choose what customers see when they shop on your website and when they check out online or in your shop."
      />

      <IfGlobalOrg
        show={false}
        fallback={
          <OrganizationContextNotice>
            Checkout settings are organization-specific. Manage them inside the right florist
            account.
          </OrganizationContextNotice>
        }
      >
        <CheckoutSettingsForm key={orgId} settings={settings} />
      </IfGlobalOrg>
    </div>
  )
}

function CheckoutSettingsForm({ settings }: { settings: CheckoutSettings }) {
  const { isReadOnly } = useIsReadOnly()
  const updateMutation = useUpdateCheckoutSettingsMutation()

  const form = useAppForm({
    defaultValues: formValues(settings),
    validators: {
      onSubmit: checkoutSettingsFormSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        if (isReadOnly) return undefined

        try {
          const savedSettings = await updateMutation.mutateAsync(value)
          toast.success('Checkout settings updated')
          // Reset after submit settles so the saved values become the form's clean state.
          setTimeout(() => {
            formApi.reset(formValues(savedSettings))
          })
          return undefined
        } catch (error) {
          toast.error('Failed to update checkout settings', {
            description: getErrorMessage(error),
          })

          return serverFieldErrors(error)
        }
      },
    },
  })

  return (
    <form.AppForm>
      <form.Form className="space-y-5">
        <Card>
          <CardHeader>
            <CardTitle>Add-ons</CardTitle>
            <CardDescription>
              Product pages show the first three add-ons by default. Every add-on is still offered
              after a customer adds an item to their cart.
            </CardDescription>
          </CardHeader>
          <CardContent className="@container">
            <form.AppField name="product_page_all_add_ons_enabled">
              {(field) => (
                <BillingBooleanSettingField
                  field={field}
                  icon={Gift}
                  title="Show All Add-ons on Product Pages"
                  description="Let customers scroll through every add-on instead of seeing just the first three."
                  disabled={isReadOnly}
                />
              )}
            </form.AppField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tips</CardTitle>
            <CardDescription>
              Changes apply right away, including to customers who are checking out now. Orders
              already placed keep any tip the customer added.
            </CardDescription>
          </CardHeader>
          <CardContent className="@container space-y-4">
            <form.AppField name="checkout_tips_enabled">
              {(field) => (
                <BillingBooleanSettingField
                  field={field}
                  icon={HandCoins}
                  title="Let Customers Add a Tip"
                  description="Show tip choices on the payment step of your website checkout."
                  disabled={isReadOnly}
                />
              )}
            </form.AppField>
            <form.AppField name="checkout_tip_split_enabled">
              {(field) => (
                <BillingBooleanSettingField
                  field={field}
                  icon={Split}
                  title="Split Tips Between Designer and Driver"
                  description="On delivery orders, customers and POS staff choose how much of the tip goes to the designer and the driver. Your Tips report shows each share. Use this if you have your own drivers."
                  disabled={isReadOnly}
                />
              )}
            </form.AppField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recipient Details</CardTitle>
            <CardDescription>
              Choose when customers must give the recipient's phone number on your website.
            </CardDescription>
          </CardHeader>
          <CardContent className="@container space-y-4">
            <form.AppField name="checkout_recipient_phone_required_for_delivery">
              {(field) => (
                <BillingBooleanSettingField
                  field={field}
                  icon={Truck}
                  title="Require Phone for Delivery"
                  description="Customers must enter the recipient's phone number for delivery orders."
                  disabled={isReadOnly}
                />
              )}
            </form.AppField>
            <form.AppField name="checkout_recipient_phone_required_for_pickup">
              {(field) => (
                <BillingBooleanSettingField
                  field={field}
                  icon={ShoppingBasket}
                  title="Require Phone for Pickup"
                  description="Customers must enter the recipient's phone number for pickup orders."
                  disabled={isReadOnly}
                />
              )}
            </form.AppField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Card Message</CardTitle>
            <CardDescription>
              The most characters customers can use on your website.
            </CardDescription>
          </CardHeader>
          <CardContent className="@container">
            <form.AppField name="checkout_card_message_max_length">
              {(field) => (
                <div className="space-y-6">
                  <field.SliderInput
                    label="Character Limit"
                    unit="characters"
                    min={CARD_MESSAGE_LIMIT_MIN}
                    max={CARD_MESSAGE_LIMIT_MAX}
                    step={5}
                    thumbAriaLabel="Card message character limit"
                    thumbAriaValueText={`${field.state.value} characters`}
                    disabled={isReadOnly}
                  />
                  <CardMessageLengthPreview maxLength={field.state.value} />
                </div>
              )}
            </form.AppField>
          </CardContent>
        </Card>

        {!isReadOnly ? <SaveBar mutation={updateMutation} /> : null}
      </form.Form>
    </form.AppForm>
  )
}

function formValues(settings: CheckoutSettings): CheckoutSettings {
  return {
    checkout_tips_enabled: settings.checkout_tips_enabled,
    checkout_tip_split_enabled: settings.checkout_tip_split_enabled,
    checkout_recipient_phone_required_for_delivery:
      settings.checkout_recipient_phone_required_for_delivery,
    checkout_recipient_phone_required_for_pickup:
      settings.checkout_recipient_phone_required_for_pickup,
    checkout_card_message_max_length: settings.checkout_card_message_max_length,
    product_page_all_add_ons_enabled: settings.product_page_all_add_ons_enabled,
  }
}
